import { z } from 'zod';

/**
 * URL search contract for the Search Rexel Files page.
 *
 * Shared by the route's `validateSearch` and by the page that navigates with
 * it. Zod strips keys it does not declare, so a filter missing here is dropped
 * from the URL and silently fails to survive a reload or a shared link —
 * keeping one schema for both sides is what stops the two drifting apart.
 */
/**
 * A YYYYMMDD search param that must name a real calendar day. The digit count
 * alone would let `20260230` through to the query as a numeric bound.
 */
const invoiceDateSchema = z
  .string()
  .regex(/^\d{8}$/)
  .refine((value) => {
    const isoDate = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    const date = new Date(`${isoDate}T00:00:00.000Z`);
    return (
      !Number.isNaN(date.getTime()) &&
      date.toISOString().slice(0, 10) === isoDate
    );
  }, 'Expected a valid YYYYMMDD date');

export const searchRexelFilesSearchSchema = z.object({
  fileType: z
    .enum(['844sd', '844d', 'itemast', 'invmas', 'vendr1', 'orderdad'])
    .optional(),
  source: z.enum(['azure', 'database']).optional(),
  company: z.string().optional(),
  division: z.string().optional(),
  branch: z.string().optional(),
  tradingPartner: z.string().optional(),
  invoiceNumber: z.string().optional(),
  // 844sd invoice-date range, YYYYMMDD.
  invoiceDateFrom: invoiceDateSchema.optional(),
  invoiceDateTo: invoiceDateSchema.optional(),
  orderNumber: z.string().optional(),
  claimNumber: z.string().optional(),
  customUse: z.string().optional(),
  supplierNumber: z.string().optional(),
  vend: z.string().optional(),
  location: z.string().optional(),
  itemNumber: z.string().optional(),
  fileDate: z.string().optional(),
  filters: z.record(z.string(), z.string()).optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export type SearchRexelFilesSearch = z.infer<
  typeof searchRexelFilesSearchSchema
>;
